import {
  collection,
  doc,
  getDoc,
  runTransaction,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../Firebase/firebaseconfig';
import { calcularPendienteDevolver } from '../helpers/devolucion';
import { getAuth } from 'firebase/auth';
import { metodoPagoPayload } from '../helpers/metodosPago';
import { toJsDate, toFechaSoloLocal, toLocalISODate } from '../helpers/fechas';
import { assertCorteAbierto } from './cajaProyectoService';

// util simple para id
const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// Normaliza número seguro
const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Avanza un mes manteniendo el "día del mes" del ancla
// (si no existe en el mes destino, usa el último día)
function addOneMonthSameDay(anchor) {
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const d = anchor.getDate();

  const targetMonth = m + 1;
  const targetYear = y + Math.floor(targetMonth / 12);
  const normMonth = ((targetMonth % 12) + 12) % 12;

  const lastDayOfTarget = new Date(targetYear, normMonth + 1, 0).getDate();
  const day = Math.min(d, lastDayOfTarget);

  const next = new Date(targetYear, normMonth, day);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addMonthsSameDay(anchor, count) {
  let current = new Date(anchor);
  current.setHours(0, 0, 0, 0);

  for (let i = 0; i < count; i++) {
    current = addOneMonthSameDay(current);
  }

  return current;
}

// Obtiene fecha ancla segura para siguiente vencimiento.
// Usa toJsDate para soportar Timestamp, Date, ISO string, {seconds} o ms.
// Si no hay nextDueDate válido, devuelve HOY (mismo comportamiento legacy).
function getAnchorDate(data) {
  // Usamos toFechaSoloLocal para evitar que un nextDueDate legacy guardado
  // como UTC midnight quede corrido un dia atras al hacer setHours local.
  // Esto preserva consistencia con lo que el usuario ve en pantalla.
  const anchor = toFechaSoloLocal(data?.nextDueDate);
  if (anchor) return anchor;
  const fallback = new Date();
  fallback.setHours(0, 0, 0, 0);
  return fallback;
}

// Detecta sólo devolución ACTIVA.
// No debe usar estados de cierre como "cerrada_devolucion".
function isDevolucionActiva(data) {
  return !!(
    data?.devolucion === true ||
    data?.estado === 'en_devolucion'
  );
}

function assertDeudaAceptaMovimiento(data, enDevolucion) {
  const estado = String(data?.estado || data?.estatus || '').toLowerCase();
  const saldoRestante = Number(data?.saldoRestante ?? data?.saldo ?? 0);

  if (estado === 'cancelada' || estado === 'cancelado') {
    throw new Error('La deuda esta cancelada y no acepta movimientos.');
  }

  if (estado === 'devuelta' || estado === 'cerrada_devolucion') {
    throw new Error('La devolucion ya esta cerrada.');
  }

  // v1.2: priorizar saldoRestante sobre campo estado. Si el saldo es > 0
  // la deuda NO esta realmente liquidada aunque el campo legacy diga lo
  // contrario (caso comun en datos importados/restaurados).
  if (!enDevolucion && saldoRestante <= 0 && estado === 'liquidada') {
    throw new Error('La deuda ya esta liquidada (saldo cero).');
  }

  if (!enDevolucion && data?.activo === false) {
    throw new Error('La deuda esta inactiva. Reactivala antes de registrar movimientos.');
  }
}

function toPaymentTimestamp(value) {
  if (!value) return Timestamp.now();

  if (value?.toDate) return value;

  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) {
    throw new Error('Fecha de pago inválida');
  }

  return Timestamp.fromDate(d);
}

function buildPagoBase({
  cantidad,
  metodo,
  fecha,
  cobradorId,
  cobradorNombre,
  cobradorEmail = '',
  referencia = '',
  notas = '',
  tipo = null,
}) {
  const nowTs = Timestamp.now();
  const metodoInfo = metodoPagoPayload(metodo);

  return {
    pagoId: rid(),
    cantidad,
    ...metodoInfo,
    fecha: toPaymentTimestamp(fecha),
    createdAt: nowTs,
    cobradorId,
    cobradorNombre,
    cobradorEmail,
    referencia,
    notas,
    ...(tipo ? { tipo } : {}),
  };
}

async function resolveCobradorInfo(extras = {}) {
  if (extras?.cobradorNombre && String(extras.cobradorNombre).trim()) {
    return {
      cobradorId: extras?.cobradorId ?? null,
      cobradorNombre: String(extras.cobradorNombre).trim(),
      cobradorEmail: extras?.cobradorEmail ?? '',
    };
  }

  const auth = getAuth();
  const user = auth.currentUser;

  const cobradorId = extras?.cobradorId ?? user?.uid ?? null;
  const fallbackEmail = extras?.cobradorEmail ?? user?.email ?? '';

  if (!cobradorId) {
    return {
      cobradorId: null,
      cobradorNombre: fallbackEmail || '—',
      cobradorEmail: fallbackEmail,
    };
  }

  try {
    const userSnap = await getDoc(doc(db, 'usuarios', cobradorId));

    if (userSnap.exists()) {
      const u = userSnap.data();
      const nombreCompleto = [u?.nombre, u?.apellidos]
        .filter(Boolean)
        .join(' ')
        .trim();

      return {
        cobradorId,
        cobradorNombre: nombreCompleto || u?.email || fallbackEmail || '—',
        cobradorEmail: u?.email || fallbackEmail || '',
      };
    }
  } catch (error) {
    console.error('Error resolviendo cobrador:', error);
  }

  return {
    cobradorId,
    cobradorNombre: user?.displayName || fallbackEmail || '—',
    cobradorEmail: fallbackEmail,
  };
}

function getPagosActuales(data) {
  return Array.isArray(data?.pagos) ? data.pagos : [];
}

function calcularSaldoNormalDespues(data, pagosActuales, pagosNuevos = []) {
  const pagosCombinados = [...pagosActuales, ...pagosNuevos];

  const enganche = toNumber(data?.enganche);
  const montoTotal = toNumber(data?.monto ?? data?.importe);

  let totalPagado = 0;
  let engancheEnPagos = false;

  for (const p of pagosCombinados) {
    const cantidad = toNumber(p?.cantidad);
    const tipo = String(p?.tipo || '').toLowerCase();

    if (cantidad > 0) {
      totalPagado += cantidad;
    }

    if (tipo === 'enganche' && cantidad > 0) {
      engancheEnPagos = true;
    }
  }

  // Sprint ENG-FIX (backwards-compat): tri-estado de engancheCobradoFuera.
  //   true       → suma (cobrado fuera explícito)
  //   false      → no suma (pactado pendiente explícito)
  //   undefined  → suma (legacy, comportamiento previo al cambio)
  if (!engancheEnPagos && data?.engancheCobradoFuera !== false) {
    totalPagado += enganche;
  }

  // CV-7: redondeo a 2 decimales para evitar 49999.99999999 fantasma
  return Math.max(Math.round((montoTotal - totalPagado) * 100) / 100, 0);
}

function getEstadoNormalPorSaldo(saldo) {
  return saldo > 0 ? 'activa' : 'liquidada';
}

function getEstadoDevolucionPorSaldo(saldo) {
  return saldo > 0 ? 'en_devolucion' : 'devuelta';
}

/**
 * Registra un pago o una devolución sobre una deuda.
 * @param {string} deudaId
 * @param {number|string} monto
 * @param {string} metodo
 * @param {object} extras
 */
export async function processPayment(deudaId, monto, metodo, extras = {}) {
  const deudaRef = doc(db, 'deudas', deudaId);
  const nowTs = Timestamp.now();
  const metodoInfo = metodoPagoPayload(metodo);

  const {
    cobradorId,
    cobradorNombre,
    cobradorEmail,
  } = await resolveCobradorInfo(extras);

  const referencia = extras?.referencia ?? '';
  const notas = extras?.notas ?? '';

  const montoAbs = Math.abs(toNumber(monto));
  if (montoAbs <= 0) {
    throw new Error('Monto inválido');
  }

   const pagoId = rid();
  const fechaPagoTs = toPaymentTimestamp(extras?.fecha);

  // CR-1: bloquear si el pago cae en un corte de caja cerrado (antes de la tx).
  await assertCortesAbiertosDeudaPreTx(deudaRef, [extras?.fecha]);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(deudaRef);

    if (!snap.exists()) {
      throw new Error('Deuda no encontrada');
    }

    const data = snap.data();
    const enDevolucion = isDevolucionActiva(data);
    assertDeudaAceptaMovimiento(data, enDevolucion);
    const pagosActuales = getPagosActuales(data);

    // ── Validación de sobrepago (ES2-2) ──────────────────────────────
    // Solo aplica a pagos normales (no devoluciones). Permite tolerancia
    // de $0.01 por redondeos en cálculos float.
    // DEVENGO-FIX 17 jun 2026: recalcular saldo en vivo con la lógica actual
    // (backwards-compat de engancheCobradoFuera). El campo data.saldoRestante
    // persistido puede estar stale si fue calculado con una versión anterior
    // del helper, lo cual bloqueaba pagos legítimos (caso reportado: deuda
    // muestra saldo $3,032 en UI pero stored $32 → rechaza pago de $3,000).
    if (!enDevolucion && !extras?.permitirSobrepago) {
      const saldoActual = calcularSaldoNormalDespues(data, pagosActuales, []);
      if (saldoActual > 0 && montoAbs > saldoActual + 0.01) {
        throw new Error(
          `Sobrepago bloqueado: monto $${montoAbs.toLocaleString()} excede el saldo restante $${saldoActual.toLocaleString()}. ` +
          `Si necesitas registrarlo de todas formas, pase el flag permitirSobrepago.`,
        );
      }
    }

    const pagoBase = {
      pagoId,
      cantidad: enDevolucion ? -montoAbs : montoAbs,
      ...metodoInfo,
      fecha: fechaPagoTs,
      createdAt: nowTs,
      cobradorId,
      cobradorNombre,
      cobradorEmail,
      referencia,
      notas,
      // tipo: se propaga desde extras para casos especiales como "enganche" al firmar contrato.
      // En devoluciones siempre se fuerza 'devolucion_pago'.
      ...(enDevolucion
        ? { tipo: 'devolucion_pago' }
        : extras?.tipo
        ? { tipo: String(extras.tipo) }
        : {}),
    };

    const anchor = getAnchorDate(data);
    const next = addOneMonthSameDay(anchor);

    if (!enDevolucion) {
      const nuevoSaldo = calcularSaldoNormalDespues(data, pagosActuales, [pagoBase]);
      const nuevoEstado = getEstadoNormalPorSaldo(nuevoSaldo);
      const cajaMovimientoId = extras?.crearMovimientoCaja === false
        ? ''
        : await createCajaMovimientoFromDeudaPayment(tx, data, pagoBase, {
            deudaId,
            monto: montoAbs,
            metodo: metodoInfo.metodo,
            referencia,
            notas,
            tipo: 'entrada',
          });
      if (cajaMovimientoId) pagoBase.cajaMovimientoId = cajaMovimientoId;

      const updatePayload = {
        pagos: [...pagosActuales, pagoBase],
                lastPaymentDate: fechaPagoTs,
        saldoRestante: nuevoSaldo,
        nextDueDate: nuevoSaldo > 0 ? Timestamp.fromDate(next) : null,
        estado: nuevoEstado,
        activo: nuevoSaldo > 0,
        devolucion: false,
        esDevolucion: false,
      };

      tx.update(deudaRef, updatePayload);

      return {
        ok: true,
        tipo: 'pago',
        pagoId,
        cajaMovimientoId,
        saldoRestante: nuevoSaldo,
        estado: nuevoEstado,
      };
    }

    const maxHoy = toNumber(calcularPendienteDevolver(data));

    if (maxHoy <= 0) {
      throw new Error('No hay monto disponible para devolver');
    }

    if (montoAbs > maxHoy) {
      throw new Error(`El monto excede lo reembolsable ($${maxHoy.toFixed(2)})`);
    }

    const pendienteDespues = Math.max(
      toNumber(
        calcularPendienteDevolver({
          ...data,
          pagos: [...pagosActuales, pagoBase],
        })
      ),
      0
    );

    const nuevoEstado = getEstadoDevolucionPorSaldo(pendienteDespues);
    const cajaMovimientoId = extras?.crearMovimientoCaja === false
      ? ''
      : await createCajaMovimientoFromDeudaPayment(tx, data, pagoBase, {
          deudaId,
          monto: montoAbs,
          metodo: metodoInfo.metodo,
          referencia,
          notas,
          tipo: 'salida',
        });
    if (cajaMovimientoId) pagoBase.cajaMovimientoId = cajaMovimientoId;

    const updatePayload = {
      pagos: [...pagosActuales, pagoBase],
              lastPaymentDate: fechaPagoTs,
      nextDueDate: pendienteDespues > 0 ? Timestamp.fromDate(next) : null,
      saldoRestante: pendienteDespues,
      devolucion: pendienteDespues > 0,
      esDevolucion: pendienteDespues > 0,
      estado: nuevoEstado,
      activo: pendienteDespues > 0,
      devolucionInfo: {
        ...(data?.devolucionInfo || {}),
        paqueteDevolucionId: data?.paqueteDevolucionId || data?.devolucionInfo?.paqueteDevolucionId || '',
        saldoAntesMovimiento: maxHoy,
        saldoDespuesMovimiento: pendienteDespues,
        ultimoPagoId: pagoBase.pagoId,
        ultimoMovimientoCajaId: cajaMovimientoId || '',
        ultimoMovimientoEn: nowTs,
      },
    };

    tx.update(deudaRef, updatePayload);

    return {
      ok: true,
      tipo: 'devolucion',
      pagoId,
      cajaMovimientoId,
      saldoRestante: pendienteDespues,
      estado: nuevoEstado,
    };
  });
}

/**
 * Registra múltiples pagos sobre una deuda en una sola transacción.
 */
export async function processPaymentsBulk(deudaId, paymentRows = [], extras = {}) {
  const deudaRef = doc(db, 'deudas', deudaId);

  const {
    cobradorId,
    cobradorNombre,
    cobradorEmail,
  } = await resolveCobradorInfo(extras);

  const rows = Array.isArray(paymentRows) ? paymentRows : [];
  if (!rows.length) {
    throw new Error('No hay pagos para registrar');
  }

  // CR-1: validar cortes de caja abiertos para todas las fechas del lote.
  await assertCortesAbiertosDeudaPreTx(deudaRef, rows.map((r) => r?.fecha));

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(deudaRef);

    if (!snap.exists()) {
      throw new Error('Deuda no encontrada');
    }

    const data = snap.data();
    const enDevolucion = isDevolucionActiva(data);
    assertDeudaAceptaMovimiento(data, enDevolucion);
    const pagosActuales = getPagosActuales(data);

    // ── Validación de sobrepago en lote (ES2-2) ─────────────────────
    // DEVENGO-FIX 17 jun 2026: recalcular saldo en vivo (ver nota en processPayment).
    if (!enDevolucion && !extras?.permitirSobrepago) {
      const saldoActual = calcularSaldoNormalDespues(data, pagosActuales, []);
      const totalLote = rows.reduce((acc, r) => acc + Math.abs(toNumber(r?.cantidad)), 0);
      if (saldoActual > 0 && totalLote > saldoActual + 0.01) {
        throw new Error(
          `Sobrepago bloqueado: total del lote $${totalLote.toLocaleString()} excede el saldo restante $${saldoActual.toLocaleString()}.`,
        );
      }
    }

    const pagosNuevos = rows.map((row) => {
      const montoAbs = Math.abs(toNumber(row?.cantidad));

      if (montoAbs <= 0) {
        throw new Error('Uno de los montos es inválido');
      }

      if (!row?.metodo) {
        throw new Error('Uno de los pagos no tiene método');
      }

      return buildPagoBase({
        cantidad: enDevolucion ? -montoAbs : montoAbs,
        ...metodoPagoPayload(row.metodo),
        fecha: row.fechaReal,
        cobradorId,
        cobradorNombre,
        referencia: row.referencia ?? '',
        notas: row.notas ?? '',
        tipo: enDevolucion ? 'devolucion_pago' : null,
        cobradorEmail,
      });
    });

    const pagosActualizados = [...pagosActuales, ...pagosNuevos];

    const fechasValidas = pagosActualizados
      .map((p) => toJsDate(p?.fecha))
      .filter(Boolean)
      .sort((a, b) => b - a);

    const lastPaymentDate = fechasValidas.length
      ? Timestamp.fromDate(fechasValidas[0])
      : Timestamp.now();

    const anchor = getAnchorDate(data);
    const next = addMonthsSameDay(anchor, pagosNuevos.length);

    if (!enDevolucion) {
      const nuevoSaldo = calcularSaldoNormalDespues(data, pagosActuales, pagosNuevos);
      const nuevoEstado = getEstadoNormalPorSaldo(nuevoSaldo);
      const cajaMovimientoIds = [];
      const cajaContext = extras?.crearMovimientoCaja === false ? null : await resolveCajaContextFromDeuda(tx, data);
      if (extras?.crearMovimientoCaja !== false) {
        for (const pagoNuevo of pagosNuevos) {
          const cajaMovimientoId = createCajaMovimientoFromResolvedContext(tx, cajaContext, data, pagoNuevo, {
            deudaId,
            monto: Math.abs(toNumber(pagoNuevo.cantidad)),
            metodo: pagoNuevo.metodo,
            referencia: pagoNuevo.referencia || '',
            notas: pagoNuevo.notas || '',
            tipo: 'entrada',
          });
          if (cajaMovimientoId) {
            pagoNuevo.cajaMovimientoId = cajaMovimientoId;
            cajaMovimientoIds.push(cajaMovimientoId);
          }
        }
      }

      const updatePayload = {
        pagos: pagosActualizados,
        lastPaymentDate,
        saldoRestante: nuevoSaldo,
        nextDueDate: nuevoSaldo > 0 ? Timestamp.fromDate(next) : null,
        estado: nuevoEstado,
        activo: nuevoSaldo > 0,
        devolucion: false,
        esDevolucion: false,
      };

      tx.update(deudaRef, updatePayload);

      return {
        ok: true,
        tipo: 'bulk_pago',
        pagosRegistrados: pagosNuevos.length,
        pagoIds: pagosNuevos.map((p) => p.pagoId),
        cajaMovimientoIds,
        saldoRestante: nuevoSaldo,
        estado: nuevoEstado,
      };
    }

    const maxHoy = toNumber(calcularPendienteDevolver(data));

    if (maxHoy <= 0) {
      throw new Error('No hay monto disponible para devolver');
    }

    const totalLote = pagosNuevos.reduce(
      (s, p) => s + Math.abs(toNumber(p.cantidad)),
      0
    );

    if (totalLote > maxHoy) {
      throw new Error(`El monto excede lo reembolsable ($${maxHoy.toFixed(2)})`);
    }

    const pendienteDespues = Math.max(
      toNumber(
        calcularPendienteDevolver({
          ...data,
          pagos: pagosActualizados,
        })
      ),
      0
    );

    const nuevoEstado = getEstadoDevolucionPorSaldo(pendienteDespues);
    const cajaMovimientoIds = [];
    const cajaContext = extras?.crearMovimientoCaja === false ? null : await resolveCajaContextFromDeuda(tx, data);
    if (extras?.crearMovimientoCaja !== false) {
      for (const pagoNuevo of pagosNuevos) {
        const cajaMovimientoId = createCajaMovimientoFromResolvedContext(tx, cajaContext, data, pagoNuevo, {
          deudaId,
          monto: Math.abs(toNumber(pagoNuevo.cantidad)),
          metodo: pagoNuevo.metodo,
          referencia: pagoNuevo.referencia || '',
          notas: pagoNuevo.notas || '',
          tipo: 'salida',
        });
        if (cajaMovimientoId) {
          pagoNuevo.cajaMovimientoId = cajaMovimientoId;
          cajaMovimientoIds.push(cajaMovimientoId);
        }
      }
    }

    const updatePayload = {
      pagos: pagosActualizados,
      lastPaymentDate,
      nextDueDate: pendienteDespues > 0 ? Timestamp.fromDate(next) : null,
      saldoRestante: pendienteDespues,
      devolucion: pendienteDespues > 0,
      esDevolucion: pendienteDespues > 0,
      estado: nuevoEstado,
      activo: pendienteDespues > 0,
      devolucionInfo: {
        ...(data?.devolucionInfo || {}),
        paqueteDevolucionId: data?.paqueteDevolucionId || data?.devolucionInfo?.paqueteDevolucionId || '',
        saldoAntesMovimiento: maxHoy,
        saldoDespuesMovimiento: pendienteDespues,
        ultimoPagoId: pagosNuevos[pagosNuevos.length - 1]?.pagoId || '',
        ultimoMovimientoCajaId: cajaMovimientoIds[cajaMovimientoIds.length - 1] || '',
        ultimoMovimientoEn: Timestamp.now(),
      },
    };

    tx.update(deudaRef, updatePayload);

    return {
      ok: true,
      tipo: 'bulk_devolucion',
      pagosRegistrados: pagosNuevos.length,
      cajaMovimientoIds,
      saldoRestante: pendienteDespues,
      estado: nuevoEstado,
    };
  });
}

const firstString = (...values) =>
  values.find((value) => typeof value === 'string' && value.trim()) || '';

const uniqueStrings = (values = []) =>
  [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))];

// HOTFIX v1.1.3: zona LOCAL para evitar off-by-one (antes usaba UTC y restaba un día)
const formatPagoDate = (timestamp) => {
  const date = toJsDate(timestamp) || new Date();
  return toLocalISODate(date);
};

function getDebtPropertyIds(data = {}) {
  return uniqueStrings([
    data.propiedadId,
    ...(Array.isArray(data.propiedadIds) ? data.propiedadIds : []),
    ...(Array.isArray(data.propiedades) ? data.propiedades.map((p) => p?.propiedadId || p?.id) : []),
  ]);
}

// CR-1: resuelve el proyectoId de una deuda FUERA de transacción (con getDoc),
// para poder validar el corte de caja antes de abrir el runTransaction
// (assertCorteAbierto hace getDocs, que no corre dentro de una tx).
async function resolveProyectoIdPreTx(data = {}) {
  if (firstString(data.proyectoId)) return firstString(data.proyectoId);
  const primaryPropertyId = getDebtPropertyIds(data)[0] || '';
  let propiedad = null;
  if (primaryPropertyId) {
    const s = await getDoc(doc(db, 'propiedades', primaryPropertyId));
    if (s.exists()) propiedad = s.data();
  }
  if (firstString(propiedad?.proyectoId)) return firstString(propiedad.proyectoId);
  const fraccionamientoId = firstString(data.fraccionamientoId, propiedad?.fraccionamientoId);
  if (fraccionamientoId) {
    const fs = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
    if (fs.exists()) return firstString(fs.data()?.proyectoId) || '';
  }
  return '';
}

// CR-1: valida que NINGUNA de las fechas de pago caiga en un corte de caja
// cerrado del proyecto, ANTES de la transacción. Solo propaga el error de
// "Período cerrado"; un fallo de lectura no debe bloquear el cobro.
async function assertCortesAbiertosDeudaPreTx(deudaRef, fechas = []) {
  try {
    const snap = await getDoc(deudaRef);
    if (!snap.exists()) return;
    const proyectoId = await resolveProyectoIdPreTx(snap.data());
    if (!proyectoId) return; // sin proyecto no se crea movimiento de caja
    for (const f of fechas) {
      const fechaMs = toJsDate(f)?.getTime() || Date.now();
      await assertCorteAbierto({ cajaScope: 'proyecto', cajaId: proyectoId, fechaMs });
    }
  } catch (e) {
    if (String(e?.message || '').startsWith('Período cerrado')) throw e;
    console.warn('[CR-1] no se pudo validar corte de caja:', e);
  }
}

async function resolveCajaContextFromDeuda(tx, data = {}) {
  const propertyIds = getDebtPropertyIds(data);
  const primaryPropertyId = propertyIds[0] || '';
  let propiedad = null;

  if (primaryPropertyId) {
    const propSnap = await tx.get(doc(db, 'propiedades', primaryPropertyId));
    if (propSnap.exists()) {
      propiedad = { id: propSnap.id, ...propSnap.data() };
    }
  }

  const deudaPropiedad = Array.isArray(data.propiedades)
    ? data.propiedades.find((p) => p?.propiedadId === primaryPropertyId || p?.id === primaryPropertyId)
    : null;

  const fraccionamientoId = firstString(
    data.fraccionamientoId,
    propiedad?.fraccionamientoId,
    deudaPropiedad?.fraccionamientoId
  );
  let fraccionamiento = null;

  if (fraccionamientoId) {
    const fraccSnap = await tx.get(doc(db, 'fraccionamientos', fraccionamientoId));
    if (fraccSnap.exists()) {
      fraccionamiento = { id: fraccSnap.id, ...fraccSnap.data() };
    }
  }

  const proyectoId = firstString(
    data.proyectoId,
    propiedad?.proyectoId,
    fraccionamiento?.proyectoId
  );

  if (!proyectoId) return null;

  let proyecto = null;
  const proyectoSnap = await tx.get(doc(db, 'proyectos', proyectoId));
  if (proyectoSnap.exists()) {
    proyecto = { id: proyectoSnap.id, ...proyectoSnap.data() };
  }

  return {
    proyectoId,
    proyectoNombre: firstString(data.proyectoNombre, propiedad?.proyectoNombre, fraccionamiento?.proyectoNombre, proyecto?.nombre),
    fraccionamientoId,
    fraccionamientoNombre: firstString(
      data.fraccionamientoNombre,
      data.fraccNombre,
      propiedad?.fraccionamientoNombre,
      propiedad?.fraccNombre,
      fraccionamiento?.nombre
    ),
    propiedadId: primaryPropertyId,
    propiedadFolio: firstString(
      data.propiedadFolio,
      propiedad?.folio,
      propiedad?.codigoHumano,
      deudaPropiedad?.folio,
      deudaPropiedad?.codigo
    ),
    propiedadIds: propertyIds,
  };
}

async function createCajaMovimientoFromDeudaPayment(tx, data, pago, {
  deudaId,
  monto,
  metodo,
  referencia = '',
  notas = '',
  tipo = 'entrada',
}) {
  const context = await resolveCajaContextFromDeuda(tx, data);
  return createCajaMovimientoFromResolvedContext(tx, context, data, pago, {
    deudaId,
    monto,
    metodo,
    referencia,
    notas,
    tipo,
  });
}

function createCajaMovimientoFromResolvedContext(tx, context, data, pago, {
  deudaId,
  monto,
  metodo,
  referencia = '',
  notas = '',
  tipo = 'entrada',
}) {
  if (!context) return '';
  const metodoInfo = metodoPagoPayload(metodo);

  const cajaRef = doc(collection(db, 'cajas_movimientos'));
  tx.set(cajaRef, {
    tipo,
    monto: Math.abs(toNumber(monto)),
    concepto: `${tipo === 'salida' ? 'Devolucion' : 'Pago'} CxC - ${data.clienteNombre || data.folio || deudaId}`,
    conceptoId: '',
    conceptoTipo: tipo === 'salida' ? 'Devolucion a cliente' : 'Cobranza de cliente',
    categoria: tipo === 'salida' ? 'devolucion' : 'cobranza',
    fecha: formatPagoDate(pago.fecha),
    fechaMs: toJsDate(pago.fecha)?.getTime() || Date.now(),
    cajaScope: 'proyecto',
    cajaId: context.proyectoId,
    clusterId: '',
    transferenciaId: '',
    transferenciaInterna: false,
    proyectoId: context.proyectoId,
    proyectoNombre: context.proyectoNombre,
    fraccionamientoId: context.fraccionamientoId,
    fraccionamientoNombre: context.fraccionamientoNombre,
    obraId: '',
    obraNombre: '',
    propiedadId: context.propiedadId,
    propiedadFolio: context.propiedadFolio,
    propiedadIds: context.propiedadIds,
    cxpId: '',
    deudaId,
    deudaPagoId: pago.pagoId,
    origenTipo: 'deuda_pago',
    origenHistorico: false,
    ...metodoInfo,
    referencia,
    notas,
    activo: true,
    cancelado: false,
    createdAt: Timestamp.now(),
    createdAtMs: Date.now(),
    createdByUid: pago.cobradorId || '',
    createdByEmail: pago.cobradorEmail || '',
    origen: 'deudas.pago',
  });
  return cajaRef.id;
}
